/**
 * ActiveViewStrip - the dismiss-proof strip shown on the operator's OWN site
 * while one of THEIR act-as ("view as") bindings is live (design.md section
 * 4.4; TASK-IMP-ADMIN-ENTRY-CHG-005). It reads "Active view: {target}" and
 * carries ONE exit, and it is the second exit affordance for the closed-tab
 * case: an operator who closed the target tab can still end the binding from
 * the admin site instead of waiting for it to expire.
 *
 * A SIBLING EXPORT OF THE BANNER'S FOLDER, NOT A SECOND SHARED COMPONENT
 * DIRECTORY. Same feature, same tokens, same catalog, same descriptor shape
 * ({@link ImpersonationBinding}) as `ImpersonationBanner`, which is why it
 * lives beside it and why EPIC-IMPERSONATION-AC-029 clause 1 can keep naming
 * the banner alone as the genuinely-new component. It has its own row in both
 * COMPONENT-INVENTORY files.
 *
 * BACKED BY THE SELF-EXIT, NEVER BY THE ADMIN REVOKE. The consumer implements
 * `onExit` with `exitImpersonationSession` - `DELETE
 * /api/v1/impersonation/sessions/{ref}` under impersonation.start
 * (SPEC-GO-API-CONTRACT-039) - which a tenant admin holds, so the SAME
 * component mounts on the tenant SPA (TASK-IMP-TENANT-ADMIN-LEG) with no extra
 * permission. Wiring it to the platform-admin revoke would silently make it
 * platform-admin-only (major finding M25).
 *
 * A PURE FUNCTION OF ITS PROPS. It fetches nothing, owns no query key
 * (RULE-077), reads no token and touches no storage. The caller's own
 * descriptor is read through the single owning module (`api/impersonation.ts`)
 * and handed down.
 *
 * DISMISS-PROOF BY DESIGN. There is no close icon, no dismiss button and no
 * Escape handler: a strip the operator could dismiss is exactly the defect it
 * exists to prevent - a forgotten live binding with no visible exit.
 *
 * PLACEMENT. A fixed strip at the BOTTOM of the viewport, one layer above the
 * app bar. The admin shell's app bar is fixed at the top, so a top strip would
 * either be covered by it or cover it; the bottom edge is free on every shell,
 * including the tenant SPA that mounts the same export. It is `role="status"`
 * with a polite live region, so a screen reader announces it WITHOUT it
 * stealing focus, and its exit is an ordinary tab stop reachable from the page
 * it overlays.
 *
 * WHAT RENDERS, PER `state` (RULE-042 plus RULE-035 clause (c)):
 * - `none`    - NOTHING. There is genuinely no binding to announce.
 * - `active`  - the strip naming the target and the exit.
 * - `exiting` - the same strip, the exit disabled and `aria-busy`.
 * - `error`   - with a binding: the strip STAYS, with a visible `role="alert"`
 *   line saying the exit failed, and the exit stays enabled as the retry.
 *   Without a binding: the strip renders the failed descriptor read LOUDLY
 *   rather than as silence, because an absent strip is indistinguishable from
 *   "nothing is live".
 * `active` / `exiting` with a `null` binding render nothing.
 *
 * @see TASK-IMP-ADMIN-ENTRY (CHG-005, and the RULE-035 DoD in its technical_criteria)
 * @see SPEC-GO-API-CONTRACT-039
 * @see SPEC-FRONTEND-RULE-035, RULE-042, RULE-046, RULE-077
 */

import React from 'react';
import type { SxProps, Theme } from '@mui/material';
import { Box, Button, Typography } from '@mui/material';
import { useLingui } from '@lingui/react/macro';
import { colors } from '../../../theme/colors';
import {
  IMPERSONATION_BANNER_MIN_HEIGHT,
  type ImpersonationBinding,
} from './ImpersonationBanner';

/** The rendering state, decided by the consumer - this component owns no query. */
export type ActiveViewStripState = 'none' | 'active' | 'exiting' | 'error';

/** Props of {@link ActiveViewStrip}. */
export interface ActiveViewStripProps {
  /**
   * The caller's OWN live binding, in the banner's descriptor shape, or `null`
   * when nothing is live (or when the descriptor read failed).
   */
  binding: ImpersonationBinding | null;
  /** Which rendering applies. See {@link ActiveViewStripState}. */
  state: ActiveViewStripState;
  /**
   * Called by the exit. The consumer implements it with the SELF-EXIT
   * (`exitImpersonationSession`, SPEC-GO-API-CONTRACT-039) and never with the
   * platform-admin revoke; this component issues no request itself.
   */
  onExit: () => void;
}

/** The WCAG 2.1 AA pointer-target floor in CSS px (RULE-046 / RULE-007). */
const MIN_TARGET_SIZE = 24;

/**
 * Focus-ring width and offset in CSS px, named for the same reason the banner
 * names it: the theme carries no focus-ring token and RULE-035 clause (c)
 * requires a VISIBLE focus indicator.
 */
const FOCUS_RING_WIDTH = 2;

/** The fixed strip. A floor height, never a fixed one; it wraps and grows (RULE-047). */
const stripSx: SxProps<Theme> = {
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  width: '100%',
  minHeight: IMPERSONATION_BANNER_MIN_HEIGHT,
  zIndex: (theme: Theme) => theme.zIndex.appBar + 1,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  columnGap: 2,
  rowGap: 0.5,
  px: 2,
  py: 1,
  borderTop: '1px solid',
  borderColor: colors.border.default,
  // Opaque: the page scrolls UNDER a fixed strip.
  backgroundColor: colors.status.warning,
  color: colors.text.primary,
};

const labelSx: SxProps<Theme> = {
  fontWeight: 600,
  color: colors.text.primary,
};

const errorSx: SxProps<Theme> = {
  color: colors.text.primary,
};

/** The exit: a real button (MUI Button), tokens only, at the target floor. */
const exitSx: SxProps<Theme> = {
  ml: 'auto',
  minHeight: MIN_TARGET_SIZE,
  minWidth: MIN_TARGET_SIZE,
  px: 1.5,
  textTransform: 'none',
  fontWeight: 600,
  borderColor: colors.text.primary,
  color: colors.text.primary,
  '&:hover': {
    borderColor: colors.text.primary,
    backgroundColor: colors.background.hover,
  },
  '&.Mui-disabled': {
    borderColor: colors.border.disabled,
    color: colors.text.disabled,
  },
  '&:focus-visible': {
    outlineStyle: 'solid',
    outlineWidth: FOCUS_RING_WIDTH,
    outlineColor: colors.interactive.active,
    outlineOffset: FOCUS_RING_WIDTH,
  },
};

/**
 * The operator-side active-view strip.
 *
 * @param props - See {@link ActiveViewStripProps}.
 */
const ActiveViewStrip: React.FC<ActiveViewStripProps> = ({ binding, state, onExit }) => {
  const { t } = useLingui();

  if (state === 'none') return null;

  // A failed descriptor read: loud, never silent.
  if (state === 'error' && !binding) {
    return (
      <Box role="status" aria-live="polite" sx={stripSx} data-testid="active-view-strip-error">
        <Typography role="alert" variant="body2" sx={labelSx}>
          {t`Your active views could not be checked. Reload the page to try again.`}
        </Typography>
      </Box>
    );
  }

  if (!binding) return null;

  // A plain local identifier, so the catalog row carries the NAMED
  // placeholder {target} on both sides (a member expression would extract as
  // a positional {0} - the 2026-09-18 empty-placeholder incident).
  const target = binding.target.display_name;
  const exiting = state === 'exiting';

  return (
    <Box role="status" aria-live="polite" sx={stripSx} data-testid="active-view-strip">
      <Typography variant="body2" sx={labelSx} data-testid="active-view-strip-label">
        {t`Active view: ${target}`}
      </Typography>

      {state === 'error' ? (
        <Typography role="alert" variant="caption" sx={errorSx} data-testid="active-view-strip-exit-error">
          {t`The view could not be exited. Try again.`}
        </Typography>
      ) : null}

      <Button
        type="button"
        variant="outlined"
        size="small"
        onClick={onExit}
        disabled={exiting}
        aria-busy={exiting}
        sx={exitSx}
        data-testid="active-view-strip-exit"
      >
        {exiting ? t`Exiting...` : t`Exit`}
      </Button>
    </Box>
  );
};

export default ActiveViewStrip;
